import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useConfirm } from 'legacy/lut/components/shared/Confirm/useConfirm'
import { getJbpBridgesCrud } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/commercialBridges/components/businessBridges/helpers/getJbpBridgesCrud'
import { getBusinessBridgeCrud } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/commercialBridges/cruds/getBusinessBridgeCrud'
import { KeyInitiativeEditor } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditor/KeyInitiativeEditor'
import { IFormKIEditor } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/models'
import { selectCurrentCompleteness } from 'legacy/lut/features/partner360/completeness'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { useSignature } from 'legacy/lut/hooks/useSignature'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import sanitize from 'sanitize-html'

import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Button } from 'core-components/library/button'
import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import { Static } from 'core-components/types'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import { Crud } from 'core/types'
import { ConfirmationDialog } from 'partner360/components/confirmationDialog/ConfirmationDialog'
import { TOUR_TARGETS } from 'partner360/configs/widgetTour/planningActivitiesTourTargets'
import { TKeyInitiative } from 'partner360/entities/keyInitiatives'
import { useRefetchPlanningCockpitData } from 'partner360/features/financialCockpit/model/hooks/useRefetchPlanningCockpitData'
import { INITIATIVES_EDITOR_TYPE_LEGACY } from 'partner360/features/keyInitiativeEditor/model/editorConfig'
import { USE_SELECTIVE_COPY_FROM_PREVIOUS_YEAR } from 'partner360/features/keyInitiatives/configs/config'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import { CopyKeyInitiativesFromPreviousYearDialog } from 'partner360/features/keyInitiatives/copyFromPreviousYear'
import { mapPreviousYearInitiativeToEditorState as mapInitiativeToEditorState } from 'partner360/features/keyInitiatives/copyFromPreviousYear/lib/mapPreviousYearInitiativeToEditorState'
import { recalculateCompleteness } from 'partner360/helpers/completeness/recalculateCompleteness'
import { useHoldPlanSectionOpen } from 'partner360/lib/planSectionBusy'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'
import { applyKeyInitiativeChanges } from 'partner360/services/keyInitiatives/applyKeyInitiativeChanges'

import { DashboardGroupBy } from '../../model/types'
import { useCopyFromPrevYear } from '../hooks/useCopyFromPrevYear'
import { useHandleShowAddNewDialog } from '../hooks/useHandleShowAddNewDialog'
import { useNewKeyInitiative } from '../hooks/useNewKeyInitiative'

import { GroupedPaginatedTable } from './components/groupedPaginatedTable/GroupedPaginatedTable'
import {
  deleteDialogIdentifier,
  KeyInitiativeDeleteDialog,
} from './components/keyInitiativeDeleteDialog/KeyInitiativeDeleteDialog'
import { ListToolbar } from './components/listToolbar/ListToolbar'
import { getColumns } from './helpers/getColumns'
import {
  getGroupedInitiativeRows,
  getInitiativeListRows,
  getListFinancialTotals,
  getListMeasureTotal,
} from './helpers/getGroupedInitiativeRows'
import { getListExportColumns, getListExportRows } from './helpers/listExport'
import { sortInitiativeListRows } from './helpers/listSort'
import { useInitiativeListFilters } from './hooks/useInitiativeListFilters'
import { useListViewSettings } from './hooks/useListViewSettings'
import { IListSort } from './models'
import { TGroupedInitiativeRow } from './models'

import styles from './KeyInitiativesListView.module.scss'

interface IInitiativesTableProps {
  data: TKeyInitiative[]
  isCurrentYearDataLoaded: boolean
  partnerId: string
  refetchKI: () => void
  isGlobal: boolean
  isRollupMode: boolean
  fYear: number
  programType: ProgramTypes

  activeCurrency: string

  actualsType: ActualsValueTypes
  actualsTypes: ActualsValueTypes[]
  onChangeActualsType: (value: ActualsValueTypes) => void
  withControls?: boolean
}

export const KeyInitiativesListView = ({
  data,
  isCurrentYearDataLoaded,
  partnerId,
  refetchKI,
  isGlobal,
  isRollupMode,
  activeCurrency,
  actualsType,
  fYear,
  programType,
  onChangeActualsType,
  actualsTypes,
  withControls,
}: IInitiativesTableProps) => {
  const { groupBy, measure, changeGroupBy, changeMeasure } = useListViewSettings()
  const [sort, setSort] = useState<IListSort>({ field: 'Name', direction: 'ascending' })
  const isEditMode = useSelector(selectPublishingMode)
  const initiativesPlanMessage =
    useSelector(selectCurrentCompleteness)?.initiativesPlanMessage?.trim() || ''
  const { signatureId } = useSignature()

  const signed = signatureId ? 1 : -1

  const { keyObjectives, kiBusinessPriorities } = useSelector(selectJBPSettings)

  const ownerId = useSelector(selectAppOwnerId)
  const dispatch = useAppDispatch()
  const refetchPlanningCockpitData = useRefetchPlanningCockpitData()
  const t = useSafetyTranslation()
  const { isPartnerUser } = useUserRoles()

  const {
    filters,
    options,
    filteredKeyInitiatives,
    changeFilter,
    clearFilters,
    quickFilter,
    resetQuickFilter,
  } = useInitiativeListFilters({ data, keyObjectives, priorities: kiBusinessPriorities })

  const canEditPartner = useMemo(() => {
    return !isPartnerUser
  }, [isPartnerUser])

  const [isLoading, setIsLoading] = useState(false)

  useHoldPlanSectionOpen(isLoading)
  const [recentlyCopiedSourceIds, setRecentlyCopiedSourceIds] = useState<Set<number>>(
    () => new Set()
  )

  const handleInitiativesCopied = useCallback((sourceEntryIds: number[]) => {
    setRecentlyCopiedSourceIds((ids) => new Set([...ids, ...sourceEntryIds]))
  }, [])

  const highlightedEntryIds = useMemo(
    () =>
      new Set(
        data
          .filter(
            (initiative) =>
              initiative.FYear === fYear &&
              initiative.PartnerId === partnerId &&
              initiative.PreviousEntryId != null &&
              recentlyCopiedSourceIds.has(initiative.PreviousEntryId)
          )
          .map((initiative) => initiative.EntryId)
      ),
    [data, fYear, partnerId, recentlyCopiedSourceIds]
  )

  const {
    showCopyFromPrevYearDialog,
    openCopyFromPrevYearDialog,
    closeCopyFromPrevYearDialog,
    handleCopyFromPrevYear,
  } = useCopyFromPrevYear({
    onChangeLoading: setIsLoading,
    partnerId,
    fetch: () => refetchKI(),
  })

  const { showAddNewDialog, handleShowNewDialog, handleCloseNewDialog } =
    useHandleShowAddNewDialog()

  const { sourceItem, resetSourceItem, setSourceItem } = useNewKeyInitiative<
    Partial<IFormKIEditor>
  >({
    handleShowNewDialog,
  })

  const { confirm } = useConfirm()

  const rows = useMemo(
    () =>
      getGroupedInitiativeRows(
        filteredKeyInitiatives,
        groupBy,
        keyObjectives,
        kiBusinessPriorities,
        actualsType,
        activeCurrency,
        measure,
        t
      ).map((row) => ({ ...row, Name: row.Name ? t(row.Name) : '' })),
    [
      filteredKeyInitiatives,
      groupBy,
      keyObjectives,
      kiBusinessPriorities,
      actualsType,
      activeCurrency,
      measure,
      t,
    ]
  )
  const sortedRows = useMemo(() => sortInitiativeListRows(rows, sort, t), [rows, sort, t])
  const { totalMeasureValue, financialTotals } = useMemo(() => {
    // Compute the grand totals before grouping: a KI may belong to several BU/priorities.
    const initiativeRows = getInitiativeListRows(
      filteredKeyInitiatives,
      keyObjectives,
      kiBusinessPriorities,
      actualsType,
      activeCurrency
    )
    return {
      totalMeasureValue: getListMeasureTotal(initiativeRows, measure),
      financialTotals: getListFinancialTotals(initiativeRows),
    }
  }, [
    filteredKeyInitiatives,
    keyObjectives,
    kiBusinessPriorities,
    actualsType,
    activeCurrency,
    measure,
  ])
  const exportRows = useMemo(() => getListExportRows(sortedRows, t), [sortedRows, t])
  const crud = useMemo(() => {
    return new Crud({
      fetcher: new Static(exportRows),
      keyField: 'rowKey',
      defaultPaging: {
        page: 0,
        pageSize: 100,
      },
    })
  }, [exportRows])

  const closeKIDialog = useCallback(() => {
    handleCloseNewDialog()
    resetSourceItem()
  }, [handleCloseNewDialog, resetSourceItem])

  const onUpdateIconClick = useCallback(
    (item: TGroupedInitiativeRow) => {
      if (!item.source) return
      // This existing mapper only normalizes editor field types; it never changes EntryId/year.
      setSourceItem(mapInitiativeToEditorState(item.source))
      handleShowNewDialog()
    },
    [handleShowNewDialog, setSourceItem]
  )

  const canEditInitiatives = useMemo(
    () => !isRollupMode && isEditMode && canEditPartner,
    [isRollupMode, isEditMode, canEditPartner]
  )
  const columns = useMemo(
    () =>
      getColumns({
        actualsType,
        activeCurrency,
        t,
        isRollupMode,
        groupBy,
        onEdit: canEditInitiatives ? onUpdateIconClick : undefined,
      }),
    [actualsType, activeCurrency, t, isRollupMode, groupBy, canEditInitiatives, onUpdateIconClick]
  )
  const exportColumns = useMemo(
    () =>
      getListExportColumns(
        getColumns({ actualsType, activeCurrency, t, isRollupMode, groupBy: 'none' }),
        t
      ),
    [actualsType, activeCurrency, t, isRollupMode]
  )
  const hasFilters =
    Boolean(quickFilter) || Object.values(filters).some((values) => values.length > 0)
  const disclaimerKey =
    groupBy === DashboardGroupBy.BusinessPriority
      ? TRANSLATION_KEYS.disclaimerBanner.groupByPriority
      : groupBy === DashboardGroupBy.Period
        ? TRANSLATION_KEYS.disclaimerBanner.groupByPeriod
        : null

  const remove = useCallback(
    async (item: TGroupedInitiativeRow) => {
      const initiative = item.source
      if (!initiative) return
      if (await confirm(deleteDialogIdentifier)) {
        setIsLoading(true)
        try {
          const newData = await applyKeyInitiativeChanges<TKeyInitiative>({
            ownerId,
            partnerId,
            programType: initiative.ProgramType?.trim() ? programType : null,
            fYear,
            action: 'Delete',
            data: initiative,
          })

          if (initiative.AddToBridges === 1) {
            getBusinessBridgeCrud().removeQueries(true)
            getJbpBridgesCrud().removeQueries(true)
          }

          // Recalculate before either reload: cockpit also supplies the plan warning.
          await recalculateCompleteness({ pid: partnerId, fYear, dispatch })
          if (newData) {
            await refetchKI()
            refetchPlanningCockpitData()
          }
        } finally {
          setIsLoading(false)
        }
      }
    },
    [
      confirm,
      dispatch,
      fYear,
      ownerId,
      partnerId,
      programType,
      refetchKI,
      refetchPlanningCockpitData,
    ]
  )

  const handleExport = useCallback(async () => {
    const exportMethod = crud.export
    if (typeof exportMethod === 'function')
      await exportMethod(
        'Key Initiatives_FY' + String(fYear).slice(-2) + '_' + programType + '_' + partnerId,
        exportColumns
      )
  }, [crud, fYear, programType, partnerId, exportColumns])

  return (
    <div className={styles.root}>
      <LoaderOverlay isVisible={isLoading} />
      {initiativesPlanMessage && (
        <div className={styles.planMessageBanner}>
          <InfoBlock
            type={InfoBlockType.Warning}
            text={<span dangerouslySetInnerHTML={{ __html: sanitize(initiativesPlanMessage) }} />}
          />
        </div>
      )}
      <div className={styles.panel}>
        <div className={styles.pageHeader}>
          <h2>
            <Translation text={TRANSLATION_KEYS.listView.title} />
          </h2>
          <RenderWhen condition={!isRollupMode && isEditMode}>
            <Button
              className={styles.createButton}
              size="md"
              icon="kbase kbase-add"
              onClick={handleShowNewDialog}
            >
              <Translation text={TRANSLATION_KEYS.dashboard.table.createNew} />
            </Button>
            <Button
              className={styles.copyButton}
              size="md"
              variant="outlined"
              color="grey"
              onClick={openCopyFromPrevYearDialog}
              disabled={!isCurrentYearDataLoaded}
            >
              <Translation text={TRANSLATION_KEYS.copyFromPreviousYear.open} />
            </Button>
          </RenderWhen>
        </div>
        {withControls && (
          <ListToolbar
            groupBy={groupBy}
            measure={measure}
            onGroupByChange={changeGroupBy}
            onMeasureChange={changeMeasure}
            actualsType={actualsType}
            actualsTypes={actualsTypes}
            onChangeActualsType={onChangeActualsType}
            filters={filters}
            options={options}
            onFilterChange={changeFilter}
            onClearFilters={clearFilters}
            onExport={handleExport}
            visibleCount={filteredKeyInitiatives.length}
            totalCount={data.length}
            isRollupMode={isRollupMode}
            quickFilter={quickFilter}
            onResetQuickFilter={resetQuickFilter}
          />
        )}
        {!withControls && (
          <Button variant="outlined" className={styles.standaloneExport} onClick={handleExport}>
            <Translation text={TRANSLATION_KEYS.dashboard.table.export} />
          </Button>
        )}
        {disclaimerKey && (
          <div className={styles.disclaimer}>
            <InfoBlock
              type={InfoBlockType.Warning}
              text={<Translation htmlAvailable text={disclaimerKey} />}
            />
          </div>
        )}
        <div data-tour-id={TOUR_TARGETS.initiativesTable}>
          <GroupedPaginatedTable
            rows={sortedRows}
            columns={columns}
            highlightedEntryIds={highlightedEntryIds}
            groupBy={groupBy}
            measure={measure}
            activeCurrency={activeCurrency}
            sort={sort}
            onSortChange={setSort}
            initiativesCount={filteredKeyInitiatives.length}
            totalMeasureValue={totalMeasureValue}
            financialTotals={financialTotals}
            onClearFilters={clearFilters}
            hasFilters={hasFilters}
            rowActionsVisible={canEditInitiatives ? 'visible' : 'hidden'}
            rowActions={[
              {
                id: 'update',
                action: onUpdateIconClick,
                title: TRANSLATION_KEYS.listView.edit,
                icon: 'kbase kbase-edit kbase-sm',
              },
              {
                id: 'remove',
                action: remove,
                title: TRANSLATION_KEYS.listView.remove,
                icon: 'kbase kbase-delete kbase-sm',
              },
            ]}
          />
        </div>
      </div>
      <RenderWhen condition={showAddNewDialog}>
        <KeyInitiativeEditor
          initialState={sourceItem}
          isDialogOpened={showAddNewDialog}
          isDataInsightPresent={programType !== ProgramTypes.Retail}
          onLoadingStateChange={setIsLoading}
          onEditCancel={closeKIDialog}
          onUpdateFinished={() => {
            closeKIDialog()
            refetchKI()
          }}
          keyInitiativesFormatOverride={isGlobal ? INITIATIVES_EDITOR_TYPE_LEGACY : undefined}
        />
      </RenderWhen>
      <KeyInitiativeDeleteDialog isCommitmentSigned={signed > 0} />
      {USE_SELECTIVE_COPY_FROM_PREVIOUS_YEAR ? (
        <CopyKeyInitiativesFromPreviousYearDialog
          show={showCopyFromPrevYearDialog}
          partnerId={partnerId}
          programType={programType}
          currentFiscalYear={fYear}
          activeCurrency={activeCurrency}
          actualsType={actualsType}
          onClose={closeCopyFromPrevYearDialog}
          onDataChanged={refetchKI}
          onCopied={() => undefined}
          onInitiativesCopied={handleInitiativesCopied}
        />
      ) : (
        <ConfirmationDialog
          onConfirm={handleCopyFromPrevYear}
          onReject={closeCopyFromPrevYearDialog}
          title={TRANSLATION_KEYS.requirements.copyKIDialog.title}
          content={TRANSLATION_KEYS.requirements.copyKIDialog.text}
          confirmBtnText={TRANSLATION_KEYS.requirements.copyKIDialog.submit}
          rejectBtnText={TRANSLATION_KEYS.requirements.copyKIDialog.cancel}
          isVisible={showCopyFromPrevYearDialog}
        />
      )}
    </div>
  )
}
